// Status tokens: every status is BOTH a colour tone and a text label. Tones map
// to the validated pill palette (in progress / open / overdue / on hold / done).
// Domain states (obligations, criticality, availability, work orders) map onto
// these five pills so the whole app uses one consistent status vocabulary.

export type Tone = 'inprogress' | 'open' | 'overdue' | 'onhold' | 'done';

export type StatusDef = { key: string; tone: Tone; labelKey: string };

/** Tailwind class pair (bg + text) per pill tone, token-backed. */
export const TONE_CLASSES: Record<Tone, string> = {
  inprogress: 'bg-st-inprogress-bg text-st-inprogress-fg',
  open: 'bg-st-open-bg text-st-open-fg',
  overdue: 'bg-st-overdue-bg text-st-overdue-fg',
  onhold: 'bg-st-onhold-bg text-st-onhold-fg',
  done: 'bg-st-done-bg text-st-done-fg',
};

/** Work-order status → pill (labels under i18n 'wo' namespace). */
export const WORK_ORDER_STATUS: Record<string, StatusDef> = {
  open: { key: 'open', tone: 'open', labelKey: 'wo.open' },
  acknowledged: { key: 'acknowledged', tone: 'open', labelKey: 'wo.acknowledged' },
  assigned: { key: 'assigned', tone: 'inprogress', labelKey: 'wo.assigned' },
  in_progress: { key: 'in_progress', tone: 'inprogress', labelKey: 'wo.inProgress' },
  completed: { key: 'completed', tone: 'done', labelKey: 'wo.completed' },
  validated: { key: 'validated', tone: 'done', labelKey: 'wo.validated' },
  closed: { key: 'closed', tone: 'done', labelKey: 'wo.closed' },
  cancelled: { key: 'cancelled', tone: 'open', labelKey: 'wo.cancelled' },
};

/** Obligation / due-date status → pill ('status' namespace). */
export const OBLIGATION_STATUS: Record<string, StatusDef> = {
  scheduled: { key: 'scheduled', tone: 'open', labelKey: 'status.scheduled' },
  due_soon: { key: 'due_soon', tone: 'onhold', labelKey: 'status.dueSoon' },
  overdue: { key: 'overdue', tone: 'overdue', labelKey: 'status.overdue' },
  done: { key: 'done', tone: 'done', labelKey: 'status.done' },
};

/** Asset criticality → pill ('criticality' namespace). */
export const CRITICALITY: Record<string, StatusDef> = {
  low: { key: 'low', tone: 'open', labelKey: 'criticality.low' },
  medium: { key: 'medium', tone: 'inprogress', labelKey: 'criticality.medium' },
  high: { key: 'high', tone: 'onhold', labelKey: 'criticality.high' },
  critical: { key: 'critical', tone: 'overdue', labelKey: 'criticality.critical' },
};

/** Asset availability → pill ('availability' namespace). */
export const AVAILABILITY: Record<string, StatusDef> = {
  available: { key: 'available', tone: 'done', labelKey: 'availability.available' },
  unavailable: { key: 'unavailable', tone: 'overdue', labelKey: 'availability.unavailable' },
  retired: { key: 'retired', tone: 'open', labelKey: 'availability.retired' },
};

/** Work-order cost-line type → i18n label key ('cost' namespace). */
export const COST_LINE_TYPE: Record<string, string> = {
  labour: 'cost.labour',
  part: 'cost.part',
  subcontract: 'cost.subcontract',
  other: 'cost.other',
};

/** Translate a cost-line type via a plain i18n `t` function (safe fallback). */
export function costLineTypeKey(lineType: string): string {
  return COST_LINE_TYPE[lineType] ?? 'cost.other';
}
